import GenericHead from 'components/GenericHead';
import useTranslate from 'contexts/TranslateContext/useTranslate';
import { Helmet } from 'react-helmet';
import { highlightsPlayerLink } from 'utils/HighlightsUtils';
import { link as playerLink, meta as playerMeta } from 'heads/player';
import {
  WIDGET_DIMENSIONS,
  WidgetDimensions,
} from 'constants/widgetDimensions';
import type { HighlightsMetadata } from 'api/highlights';

type Props = {
  highlightsMetadata: HighlightsMetadata | null;
  description: string;
  imgUrl: string;
  seedId: number;
  siteUrl: string;
  slug: string;
  title: string;
  pathname: string;
};

function PodcastHighlightsHead({
  highlightsMetadata,
  description,
  imgUrl,
  seedId,
  siteUrl,
  slug,
  title,
  pathname,
}: Props) {
  const translate = useTranslate();

  const { height, fixedWidth: width }: WidgetDimensions =
    WIDGET_DIMENSIONS.PODCAST_PROFILE ?? {};
  const podcastDescription = description?.replace(/\n+/g, ' ');

  // Use clip metadata if available, otherwise fallback to podcast metadata
  const pageTitle = highlightsMetadata?.title || title;
  const pageDescription = highlightsMetadata?.description || podcastDescription;
  const pageImage =
    highlightsMetadata?.preview_image ||
    (highlightsMetadata?.image ?
      `${highlightsMetadata.image}?ops=contain(1200,630)`
    : '') ||
    imgUrl;

  // Use podcast path as fallback if pathname isn't available
  const pagePath = pathname || `/podcast/${slug}-${seedId}`;

  return (
    <>
      <GenericHead
        deeplink={`goto/talk/show/${seedId}/highlights`}
        description={pageDescription}
        facebookImage={pageImage}
        image={pageImage}
        legacyDeeplink={`play/custom/talk/show/${seedId}/highlights`}
        ogType="video"
        title={pageTitle}
        twitterImage={pageImage}
      />
      <Helmet
        link={[
          ...playerLink({ pagePath, siteUrl, title: pageTitle }),
          // Preconnect to Highlights SDK domains for faster loading
          ...highlightsPlayerLink(),
        ]}
        meta={[
          {
            content: pageTitle,
            itemProp: 'name',
          },
          {
            content: translate(
              '{title}, Talk, Radio, Listen, On Demand, iHeartRadio, iHeart',
              {
                title: pageTitle,
              },
            ),
            name: 'keywords',
          },
          ...playerMeta({ pagePath, siteUrl, height, width }),
        ]}
      />
    </>
  );
}

export default PodcastHighlightsHead;
